import { User } from '@tassomutuo-backoffice/types';
import { CtaButton, ModalContainer } from '@tassomutuo-backoffice/ui';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { CapAutocompleteInput } from '../../../../components';
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosApiInstancePrivate } from '../../../../services/api';
import { updateSpecificCapUserCollaboratori } from '../../../../services/api/team/team';
import { toast } from 'react-toastify';

interface City {
  nome: string;
  provincia: string;
  regione: string;
  cap: string;
}

interface CityOption {
  label: string;
  value: City;
}

interface IProps {
  handleClose: () => void;
  user: User;
  capType: 'Ufficio' | '2' | '3' | '4' | '5';
}

const TeamMemberEditSpecificCapModal = ({
  handleClose,
  capType,
  user,
}: IProps) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Carica i dati della città dal CAP esistente
  useEffect(() => {
    const loadCityFromCap = async () => {
      const currentCapValue = user?.profileCollaboratori?.[`Cap${capType}`];

      if (!currentCapValue) {
        return;
      }

      try {
        setIsLoadingInitialData(true);

        // Usa la stessa API del componente CapAutocompleteInput
        const endpoint = `/v1/city/autocomplete?cap=${encodeURIComponent(
          currentCapValue
        )}`;
        const response = await axiosApiInstancePrivate.get<{
          resultsCities: City[];
        }>(endpoint);

        // Trova la città con il CAP esatto
        const cityData = response.data.resultsCities.find(
          (city) => city.cap === currentCapValue
        );

        if (cityData) {
          setSelectedCity(cityData);
        } else {
          // Se non trova dati completi, imposta solo il CAP
          setSelectedCity({
            nome: '',
            provincia: '',
            regione: '',
            cap: currentCapValue,
          });
        }
      } catch (error) {
        console.error('Errore nel caricamento dei dati della città:', error);
        // In caso di errore, imposta solo il CAP
        setSelectedCity({
          nome: '',
          provincia: '',
          regione: '',
          cap: currentCapValue,
        });
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    loadCityFromCap();
  }, [user, capType]);

  const updateUserCollaboratoriProfile = useMutation({
    mutationFn: (data: {
      userId: string;
      capType: 'Ufficio' | '2' | '3' | '4' | '5';
      cap: string | null;
      comune: string | null;
      provincia: string | null;
      regione: string | null;
    }) => {
      return updateSpecificCapUserCollaboratori(data.userId, {
        capType: data.capType,
        cap: data.cap,
        comune: data.comune,
        provincia: data.provincia,
        regione: data.regione,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UsersTeam'],
      });

      toast.success('Informazioni aggiornate con successo!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const handleCityChange = (selectedOption: CityOption | null) => {
    setSelectedCity(selectedOption?.value || null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !user.id || !selectedCity?.cap) {
      toast.error('Seleziona una città valida prima di salvare.');
      return;
    }

    try {
      setIsLoading(true);

      // Fai la chiamata per aggiornare il profilo collaboratori in base al capType
      await updateUserCollaboratoriProfile.mutateAsync({
        userId: user.id,
        capType: capType,
        cap: selectedCity.cap,
        comune: selectedCity.nome,
        provincia: selectedCity.provincia,
        regione: selectedCity.regione,
      });

      handleClose();
    } catch (error) {
      console.error('Errore durante il salvataggio:', error);
      toast.error('Errore durante il salvataggio, riprova più tardi!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !user.id) {
      toast.error('Errore: utente non valido.');
      return;
    }

    try {
      setIsDeleting(true);

      // Elimina il CAP impostando il valore a null
      await updateUserCollaboratoriProfile.mutateAsync({
        userId: user.id,
        capType: capType,
        cap: null,
        comune: null,
        provincia: null,
        regione: null,
      });
      handleClose();
    } catch (error) {
      console.error("Errore durante l'eliminazione:", error);
      toast.error("Errore durante l'eliminazione, riprova più tardi!");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const hasExistingCap = user?.profileCollaboratori?.[`Cap${capType}`];

  return (
    <ModalContainer
      open
      handleClose={handleClose}
      title={`Modifica Cap${capType} - ${user.email}`}
      containerSx={{ maxWidth: '800px' }}
      mtHeader={0}
    >
      <Box width="100%" component="form" maxHeight="60vh" onSubmit={onSubmit}>
        <Box mb={3}>
          {isLoadingInitialData ? (
            <Box display="flex" justifyContent="center" p={2}>
              <Typography variant="body2" color="textSecondary">
                Caricamento dati città...
              </Typography>
            </Box>
          ) : (
            <CapAutocompleteInput
              label={`Cerca CAP per Cap${capType}`}
              onChange={handleCityChange}
              value={selectedCity || null}
            />
          )}
        </Box>

        {/* Mostra i dettagli della città selezionata */}
        {selectedCity && (
          <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Città selezionata per {capType}:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">
                    Comune:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedCity.nome}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">
                    CAP:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedCity.cap}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">
                    Provincia:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedCity.provincia}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">
                    Regione:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedCity.regione}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        <Box display="flex" justifyContent="center" gap={2} my={4}>
          <CtaButton
            variant="contained"
            type="submit"
            loading={isLoading}
            disabled={!selectedCity?.cap || isLoadingInitialData || isDeleting}
          >
            SALVA {capType}
          </CtaButton>

          {hasExistingCap && capType !== 'Ufficio' && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteClick}
              disabled={isLoading || isLoadingInitialData || isDeleting}
            >
              ELIMINA {capType}
            </Button>
          )}
        </Box>

        {/* Dialog di conferma eliminazione */}
        <Dialog
          open={showDeleteConfirm}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Conferma eliminazione</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Sei sicuro di voler eliminare il {capType} per l'utente{' '}
              {user.email}?
              {selectedCity && (
                <>
                  <br />
                  <strong>CAP attuale: {selectedCity.cap}</strong>
                  {selectedCity.nome && (
                    <>
                      <br />
                      <strong>
                        Città: {selectedCity.nome} ({selectedCity.provincia})
                      </strong>
                    </>
                  )}
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={isDeleting}>
              Annulla
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? 'Eliminando...' : 'Elimina'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ModalContainer>
  );
};

export default TeamMemberEditSpecificCapModal;
